// @flow
import {useEffect} from 'react';
import type {Node} from 'react';
import {Box} from '@wellstone-solutions/web';
import {Outlet} from 'react-router-dom';
import {BridgeEventSources, Events, Roles} from '@wellstone-solutions/common';
import {useStores} from 'hooks/useStores';
import {useEventSource} from 'hooks/useEventSource';
import {useFeatureFlag, LD_FLAGS} from 'modules/feature-flags';
import {useMembersCounts} from 'modules/member/queries/useMembersCounts';
import {PageHeader} from 'components/PageHeader';
import {UnassignedTabBar} from 'components/UnassignedTabBar';
import {AddStaffButton} from './StaffListV2';

const UNASSIGNED_STAFF_ROLES = [Roles.PEER, Roles.COUNSELOR, Roles.ADMIN];

export const StaffRoot = (): Node => {
  const {appUIStore, eventStore} = useStores();

  useEventSource(BridgeEventSources.STAFF_PAGE);

  useEffect(() => {
    appUIStore.setBreadcrumbs([]);
    eventStore.addEvent(Events.VIEWED_STAFF_LIST);
  }, [appUIStore, eventStore]);

  const enableUnassignedUsers = useFeatureFlag(
    LD_FLAGS.ENABLE_UNASSIGNED_USERS,
  );

  const countsQuery = useMembersCounts({role: UNASSIGNED_STAFF_ROLES});
  const unassignedCount = countsQuery.data?.unassigned ?? 0;

  return (
    <Box>
      <PageHeader
        title="Staff"
        description="View staff members by Programs. Access their profiles for additional detail and actions."
        ActionButton={AddStaffButton}
      />
      {enableUnassignedUsers && (
        <UnassignedTabBar
          unassignedCount={unassignedCount}
          allPath="/staff"
          unassignedPath="/staff/unassigned"
          allLabel="All Staff"
          unassignedLabel="Unassigned Staff"
          ariaLabel="Staff list tabs"
        />
      )}
      <Outlet />
    </Box>
  );
};
